import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import {
  resolveLocalizedText,
  type LocalizedTextMap,
} from "@/lib/localizedContent";
import { AuthService, User } from "../core/auth.service";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import {
  FINAL_ASSESSMENT_PASS_RATE,
  type CrewVictoryMember,
  type CrewVictoryStatus,
  loadCrewFinalStatusForUser,
} from "../game/crew-victory";
import { localQuestionBank } from "../game/question-bank.fixture";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import { buttonClasses, cardClasses, progressBarStyle } from "../shared/ui";

const FINAL_REASSESSMENT_QUESTION_COUNT = 10;
const HARDEST_GAME_STAGE = 3;

interface AnswerOption {
  id: string;
  question_id: string;
  answer_text: string;
  answer_text_translations?: LocalizedTextMap;
  order_index: number;
  is_correct: boolean;
}

interface Question {
  id: string;
  question_text: string;
  question_text_translations?: LocalizedTextMap;
  correct_answer?: boolean;
  order_index: number;
  source?: "self_assessment" | "game";
  island_id?: string | null;
  island_order_index?: number | null;
  stage?: number | null;
  point_value?: number | null;
  complexity?: number | null;
  answer_options?: AnswerOption[];
}

type UserAnswerValue = boolean | string;
type AssessmentType = "initial" | "final";

interface UserAnswer {
  questionId: string;
  answer: UserAnswerValue;
  isCorrect: boolean;
}

interface PreviousAssessmentAttempt {
  score: number;
  total_questions: number;
  completed_at: string;
  assessment_type?: AssessmentType | string | null;
}

type QuizState = "intro" | "quiz" | "results";

function chunkArray<T>(items: T[], size: number) {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function sortFinalQuestionCandidates(left: Question, right: Question) {
  const leftIsland = Number(left.island_order_index ?? 0);
  const rightIsland = Number(right.island_order_index ?? 0);
  if (leftIsland !== rightIsland) return rightIsland - leftIsland;

  const leftStage = Number(left.stage ?? 0);
  const rightStage = Number(right.stage ?? 0);
  if (leftStage !== rightStage) return rightStage - leftStage;

  const leftDifficulty = Number(left.complexity ?? left.point_value ?? 0);
  const rightDifficulty = Number(right.complexity ?? right.point_value ?? 0);
  if (leftDifficulty !== rightDifficulty) return rightDifficulty - leftDifficulty;

  return Number(left.order_index ?? 0) - Number(right.order_index ?? 0);
}

function groupAnswersByQuestion(answerRows: AnswerOption[]) {
  const answersByQuestion = new Map<string, AnswerOption[]>();

  answerRows.forEach((answer) => {
    if (!answer.question_id) return;
    const answers = answersByQuestion.get(answer.question_id) ?? [];
    answers.push(answer);
    answersByQuestion.set(answer.question_id, answers);
  });

  return answersByQuestion;
}

function isCompleteFinalQuestion(question: Question) {
  const answers = question.answer_options ?? [];
  return (
    question.question_text.trim().length > 0 &&
    answers.length >= 2 &&
    answers.some((answer) => answer.is_correct)
  );
}

@Component({
  selector: "app-self-assessment-page",
  standalone: true,
  imports: [CommonModule, LanguageSelectorComponent, LucideDynamicIcon],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="compass"
        class="h-12 w-12 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div *ngIf="!loading" class="min-h-screen ocean-gradient">
      <div class="fixed right-4 top-4 z-50">
        <app-language-selector [compact]="true" className="bg-background/80" />
      </div>

      <section
        *ngIf="quizState === 'intro'"
        class="min-h-screen flex items-center justify-center p-4 animate-in fade-in"
      >
        <div
          [class]="
            cardClasses(
              'max-w-lg w-full p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-2 border-primary/20'
            )
          "
        >
          <div class="text-center">
            <div
              class="w-20 h-20 mx-auto mb-6 rounded-full tropical-gradient flex items-center justify-center shadow-lg"
            >
              <svg
                lucideIcon="clipboard-list"
                class="h-10 w-10 text-primary-foreground"
              ></svg>
            </div>

            <h1 class="text-3xl font-bold text-foreground mb-3">
              {{ language.t("assessment.title") }}
            </h1>
            <p class="text-muted-foreground mb-6">
              {{ language.t("assessment.description") }}
            </p>

            <div
              *ngIf="previousAttempts.length > 0"
              class="mb-6 p-4 rounded-lg bg-secondary/20 border border-secondary/30"
            >
              <h3 class="text-sm font-semibold text-foreground mb-2">
                {{
                  finalAssessmentAvailable
                    ? language.t("assessment.finalTitle")
                    : language.t("assessment.completedOnce")
                }}
              </h3>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <div
                  *ngFor="let attempt of previousAttempts.slice(0, 1)"
                  class="flex justify-between text-sm text-muted-foreground"
                >
                  <span>{{ attempt.completed_at | date }}</span>
                  <span class="font-medium text-foreground"
                    >{{ attempt.score }}/{{ attempt.total_questions }}</span
                  >
                </div>
              </div>
              <p
                *ngIf="finalAssessmentLocked"
                class="mt-3 text-xs text-muted-foreground"
              >
                {{ language.t("assessment.noRetake") }}
              </p>
              <div
                *ngIf="finalAssessmentLocked"
                class="mt-3 rounded-md border border-border bg-background/70 p-3 text-left text-xs"
              >
                <p class="font-semibold text-foreground">
                  {{ language.t("winner.finalReassessmentLocked") }}
                </p>
                <p class="mt-1 text-muted-foreground">
                  {{ language.t("winner.finalLockedDescription") }}
                </p>
              </div>
              <p
                *ngIf="finalAssessmentAvailable && !finalAssessmentPassed"
                class="mt-3 text-xs text-muted-foreground"
              >
                {{
                  language.t("assessment.finalDescription", {
                    rate: finalAssessmentPassRate,
                  })
                }}
              </p>
              <p
                *ngIf="finalAssessmentPassed"
                class="mt-3 text-xs text-accent"
              >
                {{ language.t("assessment.finalComplete") }}
              </p>
            </div>

            <div class="flex gap-3 justify-center">
              <button
                [class]="buttonClasses('outline')"
                (click)="router.navigateByUrl('/')"
              >
                <svg lucideIcon="home" class="h-4 w-4"></svg>
                {{ language.t("common.backToMap") }}
              </button>
              <button
                *ngIf="canStartAssessment"
                [class]="
                  buttonClasses(
                    'default',
                    'default',
                    'tropical-gradient text-primary-foreground'
                  )
                "
                (click)="startQuiz()"
              >
                {{
                  finalAssessmentAvailable && previousAttempts.length > 0
                    ? language.t("assessment.startFinal")
                    : language.t("assessment.start")
                }}
                <svg lucideIcon="arrow-right" class="h-4 w-4"></svg>
              </button>
              <button
                *ngIf="finalAssessmentLocked"
                [class]="buttonClasses('outline')"
                disabled
              >
                <svg lucideIcon="lock" class="h-4 w-4"></svg>
                {{ language.t("winner.finalReassessmentLocked") }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section
        *ngIf="quizState === 'quiz' && currentQuestion"
        class="min-h-screen flex flex-col items-center justify-center p-4 animate-in fade-in"
      >
        <div
          [class]="
            cardClasses(
              'max-w-2xl w-full p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-2 border-primary/20'
            )
          "
        >
          <div class="mb-6">
            <div
              class="flex justify-between text-sm text-muted-foreground mb-2"
            >
              <span>{{
                language.t("assessment.questionOf", {
                  current: currentQuestionIndex + 1,
                  total: questions.length,
                })
              }}</span>
              <span>{{ progressPercentage | number: "1.0-0" }}%</span>
            </div>
            <div
              class="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
            >
              <div
                class="h-full w-full flex-1 bg-primary transition-all"
                [ngStyle]="progressBarStyle(progressPercentage)"
              ></div>
            </div>
          </div>

          <div
            class="text-center mb-8 animate-in slide-in-from-right-4 fade-in"
          >
            <h2
              class="text-xl md:text-2xl font-semibold text-foreground leading-relaxed"
            >
              {{ currentQuestion.question_text }}
            </h2>
          </div>

          <ng-container
            *ngIf="currentQuestion.answer_options?.length; else trueFalseOptions"
          >
            <div class="grid gap-3 mb-6">
              <button
                *ngFor="
                  let answerOption of currentQuestion.answer_options;
                  let answerIndex = index
                "
                [class]="answerOptionButtonClass(answerOption)"
                (click)="submitAnswer(answerOption.id)"
                [disabled]="selectedAnswer !== null"
              >
                <span
                  class="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-current text-xs font-bold"
                >
                  {{ answerOptionLabel(answerIndex) }}
                </span>
                <span class="min-w-0 text-left leading-snug">
                  {{ answerOption.answer_text }}
                </span>
              </button>
            </div>
          </ng-container>

          <ng-template #trueFalseOptions>
            <div class="grid grid-cols-2 gap-4 mb-6">
              <button
                [class]="answerButtonClass(true)"
                (click)="submitAnswer(true)"
                [disabled]="selectedAnswer !== null"
              >
                <svg lucideIcon="circle-check" class="h-5 w-5 mr-2"></svg>
                {{ language.t("assessment.true") }}
              </button>
              <button
                [class]="answerButtonClass(false)"
                (click)="submitAnswer(false)"
                [disabled]="selectedAnswer !== null"
              >
                <svg lucideIcon="circle-x" class="h-5 w-5 mr-2"></svg>
                {{ language.t("assessment.false") }}
              </button>
            </div>
          </ng-template>

          <div
            *ngIf="showFeedback"
            class="text-center animate-in slide-in-from-bottom-4 fade-in"
          >
            <div
              class="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              [ngClass]="
                selectedAnswer === currentQuestion.correct_answer
                  ? 'bg-accent/20 text-accent'
                  : 'bg-destructive/20 text-destructive'
              "
            >
              <ng-container
                *ngIf="
                  selectedAnswer === currentQuestion.correct_answer;
                  else wrongAnswer
                "
              >
                <svg lucideIcon="circle-check" class="h-5 w-5"></svg>
                {{ language.t("island.correct") }}
              </ng-container>
              <ng-template #wrongAnswer>
                <svg lucideIcon="circle-x" class="h-5 w-5"></svg>
                {{
                  language.t("assessment.incorrectAnswer", {
                    answer: correctAnswerLabel(),
                  })
                }}
              </ng-template>
            </div>
            <button
              [class]="
                buttonClasses(
                  'default',
                  'default',
                  'tropical-gradient text-primary-foreground'
                )
              "
              (click)="nextQuestion()"
            >
              {{
                currentQuestionIndex < questions.length - 1
                  ? language.t("island.nextQuestion")
                  : language.t("assessment.seeResults")
              }}
              <svg lucideIcon="arrow-right" class="h-4 w-4 ml-2"></svg>
            </button>
          </div>
        </div>
      </section>

      <section
        *ngIf="quizState === 'results'"
        class="min-h-screen flex items-center justify-center p-4 animate-in fade-in"
      >
        <div
          [class]="
            cardClasses(
              'max-w-lg w-full p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-2 border-primary/20'
            )
          "
        >
          <div class="text-center">
            <div
              class="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg"
              [ngClass]="
                finalScore >= 7
                  ? 'bg-accent'
                  : finalScore >= 5
                    ? 'bg-primary'
                    : 'bg-secondary'
              "
            >
              <span class="text-4xl font-bold text-primary-foreground"
                >{{ finalScore }}/{{ questions.length }}</span
              >
            </div>

            <h1 class="text-3xl font-bold text-foreground mb-3">
              {{ resultTitle }}
            </h1>
            <p class="text-muted-foreground mb-6">{{ resultDescription }}</p>

            <div
              *ngIf="finalAssessmentAvailable"
              class="mb-6 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground"
            >
              <p class="font-medium text-foreground">
                {{ language.t("assessment.finalTitle") }}:
                {{ finalScorePercentage | number: "1.0-0" }}%
              </p>
              <p class="mt-1">
                {{
                  finalScorePercentage >= finalAssessmentPassRate
                    ? language.t("assessment.finalPassedResult")
                    : language.t("assessment.finalRetryResult", {
                        rate: finalAssessmentPassRate,
                      })
                }}
              </p>
            </div>

            <div class="flex gap-3 justify-center">
              <button
                *ngIf="
                  finalAssessmentAvailable &&
                  finalScorePercentage < finalAssessmentPassRate
                "
                [class]="buttonClasses('outline')"
                (click)="startQuiz()"
              >
                <svg lucideIcon="rotate-ccw" class="h-4 w-4"></svg>
                {{ language.t("assessment.retakeShort") }}
              </button>
              <button
                [class]="
                  buttonClasses(
                    'default',
                    'default',
                    'tropical-gradient text-primary-foreground'
                  )
                "
                (click)="router.navigateByUrl(finalAssessmentAvailable ? '/winner' : '/')"
              >
                <svg
                  [lucideIcon]="finalAssessmentAvailable ? 'crown' : 'home'"
                  class="h-4 w-4"
                ></svg>
                {{
                  finalAssessmentAvailable
                    ? language.t("assessment.viewVictory")
                    : language.t("assessment.exploreIslands")
                }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
})
export class SelfAssessmentPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  questions: Question[] = [];
  loading = true;
  quizState: QuizState = "intro";
  currentQuestionIndex = 0;
  userAnswers: UserAnswer[] = [];
  selectedAnswer: UserAnswerValue | null = null;
  showFeedback = false;
  previousAttempts: PreviousAssessmentAttempt[] = [];
  crewVictoryStatus: CrewVictoryStatus | null = null;
  finalAssessmentPassRate = FINAL_ASSESSMENT_PASS_RATE;
  private subscription?: Subscription;

  ngOnInit() {
    this.subscription = this.auth.user$.subscribe((user) => {
      this.user = user;
      this.changeDetector.detectChanges();
      if (!user) {
        void this.router.navigateByUrl("/auth");
        return;
      }
      void this.fetchData();
    });

    this.language.language$.subscribe(() => {
      this.changeDetector.detectChanges();
      if (this.user) void this.fetchData(false);
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  get currentQuestion() {
    return this.questions[this.currentQuestionIndex];
  }

  get finalScore() {
    return this.userAnswers.filter((answer) => answer.isCorrect).length;
  }

  get finalScorePercentage() {
    return this.questions.length > 0
      ? (this.finalScore / this.questions.length) * 100
      : 0;
  }

  get progressPercentage() {
    return this.questions.length > 0
      ? ((this.currentQuestionIndex + 1) / this.questions.length) * 100
      : 0;
  }

  get resultTitle() {
    return this.finalScore >= 8
      ? this.language.t("assessment.excellent")
      : this.finalScore >= 6
        ? this.language.t("assessment.goodJob")
        : this.finalScore >= 4
          ? this.language.t("assessment.niceTry")
          : this.language.t("assessment.keepLearning");
  }

  get resultDescription() {
    if (this.finalAssessmentAvailable) {
      return this.finalScorePercentage >= this.finalAssessmentPassRate
        ? this.language.t("assessment.finalPassedResult")
        : this.language.t("assessment.finalRetryResult", {
            rate: this.finalAssessmentPassRate,
          });
    }

    return this.finalScore >= 8
      ? this.language.t("assessment.strongFoundation")
      : this.finalScore >= 6
        ? this.language.t("assessment.rightTrack")
        : this.language.t("assessment.buildKnowledge");
  }

  get currentMemberFinalStatus(): CrewVictoryMember | null {
    return (
      this.crewVictoryStatus?.members.find(
        (member) => member.user_id === this.user?.id,
      ) ?? null
    );
  }

  get finalAssessmentAvailable() {
    return Boolean(this.crewVictoryStatus?.completed_all_islands);
  }

  get finalAssessmentPassed() {
    return Boolean(this.currentMemberFinalStatus?.final_assessment_passed);
  }

  get finalAssessmentLocked() {
    return this.initialAssessmentCompleted && !this.finalAssessmentAvailable;
  }

  get canStartAssessment() {
    if (!this.initialAssessmentCompleted) return true;
    return this.finalAssessmentAvailable && !this.finalAssessmentPassed;
  }

  get initialAssessmentCompleted() {
    return this.previousAttempts.some(
      (attempt) => attempt.assessment_type !== "final",
    );
  }

  async fetchData(showSpinner = true) {
    if (!this.user) return;

    if (showSpinner) {
      this.loading = true;
      this.changeDetector.detectChanges();
    }
    try {
      const [questionsResponse, attemptsResponse, crewVictoryStatus] =
        await Promise.all([
          backend
            .from("self_assessment_questions")
            .select("*")
            .order("order_index", { ascending: true }),
          backend
            .from("self_assessment_attempts")
            .select("score, total_questions, completed_at, assessment_type")
            .eq("user_id", this.user.id)
            .order("completed_at", { ascending: false }),
          loadCrewFinalStatusForUser(this.user.id),
        ]);

      if (questionsResponse.error) throw questionsResponse.error;
      if (attemptsResponse.error) throw attemptsResponse.error;
      const selfAssessmentQuestions = (
        (questionsResponse.data as unknown as Question[]) || []
      ).map((question) => ({
        ...question,
        source: "self_assessment" as const,
        question_text: resolveLocalizedText(
          question.question_text,
          question.question_text_translations,
          this.language.language,
        ),
      }));
      this.previousAttempts =
        (attemptsResponse.data as unknown as PreviousAssessmentAttempt[]) || [];
      this.crewVictoryStatus = crewVictoryStatus;
      this.questions = this.finalAssessmentAvailable
        ? await this.loadFinalReassessmentQuestions()
        : selfAssessmentQuestions;
    } catch (error) {
      console.error("Error fetching data:", error);
      this.toast.error(this.language.t("assessment.loadError"));
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  startQuiz() {
    if (!this.canStartAssessment) {
      this.toast.info(
        this.finalAssessmentPassed
          ? this.language.t("assessment.finalComplete")
          : this.language.t("assessment.noRetake"),
      );
      return;
    }

    this.quizState = "quiz";
    this.currentQuestionIndex = 0;
    this.userAnswers = [];
    this.selectedAnswer = null;
    this.showFeedback = false;
  }

  submitAnswer(answer: UserAnswerValue) {
    if (this.selectedAnswer !== null || !this.currentQuestion) return;

    this.selectedAnswer = answer;
    const isCorrect = this.isCorrectAnswer(this.currentQuestion, answer);
    this.userAnswers = [
      ...this.userAnswers,
      {
        questionId: this.currentQuestion.id,
        answer,
        isCorrect,
      },
    ];
    this.showFeedback = true;
  }

  async nextQuestion() {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.currentQuestionIndex += 1;
      this.selectedAnswer = null;
      this.showFeedback = false;
      return;
    }

    const score = this.userAnswers.filter((answer) => answer.isCorrect).length;

    try {
      if (!this.canStartAssessment) {
        this.quizState = "intro";
        this.toast.info(
          this.finalAssessmentPassed
            ? this.language.t("assessment.finalComplete")
            : this.language.t("assessment.noRetake"),
        );
        return;
      }

      const { error } = await backend.from("self_assessment_attempts").insert({
        user_id: this.user!.id,
        score,
        total_questions: this.questions.length,
        assessment_type: this.finalAssessmentAvailable ? "final" : "initial",
      });

      if (error) throw error;
      this.previousAttempts = [
        {
          score,
          total_questions: this.questions.length,
          completed_at: new Date().toISOString(),
          assessment_type: this.finalAssessmentAvailable ? "final" : "initial",
        },
        ...this.previousAttempts,
      ];
      if (this.user) {
        this.crewVictoryStatus = await loadCrewFinalStatusForUser(this.user.id);
      }
    } catch (error) {
      console.error("Error saving attempt:", error);
      this.toast.error(this.language.t("assessment.saveError"));
    }

    this.quizState = "results";
  }

  private async loadFinalReassessmentQuestions() {
    const remoteQuestions = await this.loadRemoteFinalReassessmentQuestions();
    if (remoteQuestions.length > 0) return remoteQuestions;

    return this.loadLocalFinalReassessmentQuestions();
  }

  private async loadRemoteFinalReassessmentQuestions() {
    try {
      const { data: islandsData, error: islandsError } = await backend
        .from("islands")
        .select("id, order_index, is_active")
        .order("order_index", { ascending: false });

      if (islandsError) throw islandsError;
      const islandOrderById = new Map(
        ((islandsData as { id: string; order_index?: number | null }[]) || [])
          .filter((island) => island.id)
          .map((island) => [island.id, Number(island.order_index ?? 0)]),
      );
      const activeIslandIds = new Set(
        ((islandsData as {
          id: string;
          is_active?: boolean | null;
        }[]) || [])
          .filter((island) => island.id && island.is_active !== false)
          .map((island) => island.id),
      );

      const { data: questionsData, error: questionsError } = await backend
        .from("questions")
        .select(
          "id, island_id, question_text, question_text_translations, stage, point_value, order_index",
        )
        .eq("stage", HARDEST_GAME_STAGE);

      if (questionsError) throw questionsError;

      const candidates = ((questionsData as unknown as Question[]) || [])
        .filter(
          (question) =>
            question.id &&
            question.island_id &&
            activeIslandIds.has(question.island_id),
        )
        .map((question) => ({
          ...question,
          source: "game" as const,
          island_order_index: islandOrderById.get(question.island_id ?? "") ?? 0,
          point_value: Number(question.point_value ?? 0),
          stage: Number(question.stage ?? HARDEST_GAME_STAGE),
        }))
        .sort(sortFinalQuestionCandidates);

      if (candidates.length === 0) return [];

      const candidateIds = candidates.map((question) => question.id);
      const answerRows: AnswerOption[] = [];
      for (const questionIdChunk of chunkArray(candidateIds, 120)) {
        const { data: answersData, error: answersError } = await backend
          .from("answers")
          .select(
            "id, question_id, answer_text, answer_text_translations, order_index, is_correct",
          )
          .in("question_id", questionIdChunk)
          .order("order_index");

        if (answersError) throw answersError;
        answerRows.push(...((answersData as unknown as AnswerOption[]) || []));
      }

      const answersByQuestion = groupAnswersByQuestion(answerRows);
      return candidates
        .map((question) =>
          this.toFinalQuestion(
            question,
            answersByQuestion.get(question.id) ?? [],
          ),
        )
        .filter((question) => isCompleteFinalQuestion(question))
        .slice(0, FINAL_REASSESSMENT_QUESTION_COUNT);
    } catch (error) {
      console.warn("Could not load hardest remote final reassessment:", error);
      return [];
    }
  }

  private loadLocalFinalReassessmentQuestions() {
    const stationOrderById = new Map(
      localQuestionBank.stations.map((station) => [
        station.id,
        station.order_index,
      ]),
    );

    return localQuestionBank.questions
      .map((question, index) => {
        const localizedQuestion =
          question.languages[this.language.language] ?? question.languages.en;
        const localizedOptions = localizedQuestion?.options as
          | Record<string, string>
          | undefined;
        const answerOptions = question.answers
          .map((answer): AnswerOption => ({
            id: answer.id,
            question_id: answer.question_id,
            answer_text:
              localizedOptions?.[answer.option_key] ?? answer.answer_text,
            order_index: answer.order_index,
            is_correct: answer.is_correct,
          }))
          .sort((left, right) => left.order_index - right.order_index);

        return {
          id: question.id,
          question_text:
            localizedQuestion?.question ?? question.question_text,
          order_index: index,
          source: "game" as const,
          island_order_index: stationOrderById.get(question.station_id) ?? 0,
          stage: question.stage,
          point_value: question.complexity,
          complexity: question.complexity,
          answer_options: answerOptions,
        };
      })
      .filter((question) => question.stage === HARDEST_GAME_STAGE)
      .filter(isCompleteFinalQuestion)
      .sort(sortFinalQuestionCandidates)
      .slice(0, FINAL_REASSESSMENT_QUESTION_COUNT);
  }

  private toFinalQuestion(question: Question, answerOptions: AnswerOption[]) {
    return {
      ...question,
      question_text: resolveLocalizedText(
        question.question_text,
        question.question_text_translations,
        this.language.language,
      ),
      answer_options: answerOptions
        .map((answer) => ({
          ...answer,
          answer_text: resolveLocalizedText(
            answer.answer_text,
            answer.answer_text_translations,
            this.language.language,
          ),
        }))
        .sort((left, right) => left.order_index - right.order_index),
    };
  }

  private isCorrectAnswer(question: Question, answer: UserAnswerValue) {
    if (question.answer_options?.length) {
      return (
        typeof answer === "string" &&
        question.answer_options.some(
          (option) => option.id === answer && option.is_correct,
        )
      );
    }

    return typeof answer === "boolean" && answer === question.correct_answer;
  }

  correctAnswerLabel(question = this.currentQuestion) {
    const correctOption = question?.answer_options?.find(
      (option) => option.is_correct,
    );
    if (correctOption) return correctOption.answer_text;

    return question?.correct_answer
      ? this.language.t("assessment.true")
      : this.language.t("assessment.false");
  }

  answerOptionLabel(index: number) {
    return String.fromCharCode(65 + index);
  }

  answerButtonClass(answer: boolean) {
    const selected = this.selectedAnswer === answer;
    const correct = this.currentQuestion?.correct_answer === answer;
    const revealCorrect = this.selectedAnswer !== null && correct;
    const variantClass = selected
      ? correct
        ? "bg-accent text-accent-foreground hover:bg-accent"
        : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
      : revealCorrect
        ? "border border-accent bg-accent/10 text-accent"
      : "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground";

    return [
      "inline-flex h-16 items-center justify-center rounded-md px-8 text-lg font-semibold transition-all disabled:pointer-events-none disabled:opacity-50",
      this.selectedAnswer === null ? "hover:scale-105" : "",
      variantClass,
    ].join(" ");
  }

  answerOptionButtonClass(answerOption: AnswerOption) {
    const selected = this.selectedAnswer === answerOption.id;
    const revealCorrect = this.selectedAnswer !== null && answerOption.is_correct;
    const variantClass = selected
      ? answerOption.is_correct
        ? "bg-accent text-accent-foreground hover:bg-accent"
        : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
      : revealCorrect
        ? "border border-accent bg-accent/10 text-accent"
        : "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground";

    return [
      "inline-flex min-h-16 w-full items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-70",
      this.selectedAnswer === null ? "hover:scale-[1.01]" : "",
      variantClass,
    ].join(" ");
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  progressBarStyle = progressBarStyle;
}
